/**
 * seanceShare.js — Encodage / décodage compact d'une séance pour partage URL.
 *
 * Approche : URL fragment (#data=...) avec JSON minifié + base64 URL-safe.
 * Reste 100% client (pas de backend, pas de DB). Le payload typique fait
 * 200-800 octets → compatible QR code standard.
 *
 * Le fragment garantit qu'aucune donnée n'est envoyée au serveur
 * (les fragments d'URL ne sont jamais transmis par les requêtes HTTP).
 */

/* ── Helpers base64 URL-safe ─────────────────────────────────────── */

function toBase64Url(str) {
  // btoa accepte uniquement de l'ASCII → on encode d'abord en UTF-8.
  const utf8 = unescape(encodeURIComponent(str));
  return btoa(utf8).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(str) {
  let b64 = String(str).replace(/-/g, '+').replace(/_/g, '/');
  while (b64.length % 4) b64 += '=';
  const utf8 = atob(b64);
  return decodeURIComponent(escape(utf8));
}

/* ── Encodage / décodage ─────────────────────────────────────────── */

/**
 * Sérialise une séance en payload compact (clés courtes pour minimiser la taille).
 * @param {Object} args
 * @param {string} args.code               code REMC (ex. C2-d)
 * @param {Object} args.state              état brut de useSeanceLive
 * @returns {string} payload base64 URL-safe (ne contient ni JSON ni nom personnel)
 */
export function encodeSeancePayload({ code, state }) {
  if (!code || !state) return '';
  // Clés courtes pour réduire la taille (cf. RFC QR < 3kB)
  const payload = {
    v: 1,
    c: code,
    b: state.briefingNotes || '',
    g: state.guidageNotes || '',
    s: state.critereStatuts || {},
    p: state.passages || 0,
    sa: state.startedAt || null,
    ua: state.updatedAt || null,
  };
  const json = JSON.stringify(payload);
  return toBase64Url(json);
}

/**
 * Décode un payload partagé.
 * @param {string} encoded
 * @returns {{ code: string, state: Object } | null}
 */
export function decodeSeancePayload(encoded) {
  if (!encoded) return null;
  try {
    const json = fromBase64Url(encoded);
    const data = JSON.parse(json);
    if (!data || data.v !== 1 || !data.c) return null;
    return {
      code: data.c,
      state: {
        briefingNotes: data.b || '',
        guidageNotes: data.g || '',
        critereStatuts: data.s || {},
        passages: data.p || 0,
        startedAt: data.sa || null,
        updatedAt: data.ua || null,
      },
    };
  } catch {
    return null;
  }
}

/**
 * Construit l'URL publique de récap à partir du payload.
 * Utilise le fragment URL (#) pour garantir la confidentialité côté serveur.
 */
export function buildShareUrl({ code, state, origin }) {
  const data = encodeSeancePayload({ code, state });
  if (!data) return '';
  const base = origin || (typeof window !== 'undefined' ? window.location.origin : '');
  return `${base}/seance-recap#data=${data}`;
}

/**
 * Lit le payload depuis l'URL courante (fragment ou query string).
 */
export function readSeanceFromUrl() {
  if (typeof window === 'undefined') return null;
  const hash = window.location.hash || '';
  const m = hash.match(/data=([A-Za-z0-9_-]+)/);
  if (m) return decodeSeancePayload(m[1]);
  // fallback ?data=... pour compatibilité éventuelle
  const params = new URLSearchParams(window.location.search);
  const q = params.get('data');
  if (q) return decodeSeancePayload(q);
  return null;
}
